import { type ReactNode, useId } from 'react';

import type { Spacing } from '@shieldpay/theme-provider-ui/theme/types';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import CopyIcon from '../icons/CopyIcon';

import { useClipboard } from '../../hooks/use-clipboard/use-clipboard';
import { Box } from '../box/box';
import { Text } from '../text/text';

import * as styles from './copy-text-icon.css';

export type CopyTextIconProps = {
  children: string;
  idleTooltipText: ReactNode;
  copiedTooltipText: ReactNode;
  variant?: 'default' | 'tag';
  maxWidth?: Spacing;
};

const tagBackground = {
  idle: 'neutral100',
  copied: 'success200',
} as const;

export const CopyTextIcon = ({
  children,
  idleTooltipText,
  copiedTooltipText,
  variant = 'default',
  maxWidth,
}: CopyTextIconProps) => {
  const { state, copyToClipboard } = useClipboard(children);

  const id = useId();

  return (
    <Box
      alignContent={{ y: 'center' }}
      className={classFilter([
        styles.button,
        variant === 'tag' && [styles.tagVariants[state], styles.tag],
      ])}
      background={variant === 'tag' ? tagBackground[state] : undefined}
      size={{ maxWidth }}
      rounded
      Component="button"
      onClick={copyToClipboard}
      padding={variant === 'tag' ? { x: 'baseNeg4' } : 'none'}
      spacing="baseNeg5"
      stack="row"
      variant="inline-flex"
      type="button"
    >
      <Text
        truncate
        variant="caption"
        className={classFilter([
          variant === 'default' && state === 'idle' && styles.idleText,
        ])}
        aria-describedby={id}
      >
        {children}
      </Text>

      <Box
        alignContent={{ x: 'center' }}
        className={classFilter([
          styles.iconWrapper,
          styles.iconWrapperVariants[state],
        ])}
        color="neutral300"
      >
        <CopyIcon size="xs" />
        <Text
          className={classFilter([
            styles.tooltip,
            styles.tooltipFirefoxTextBugWorkaround,
          ])}
          color="neutral000"
          padding="baseNeg6"
          role="tooltip"
          id={id}
          truncate
          variant="captionSmall"
        >
          {state === 'idle' ? idleTooltipText : copiedTooltipText}
        </Text>
      </Box>
    </Box>
  );
};
